import type { Session } from './contract.gen'
import { ApiError } from './errors'
import { serverClock } from './clock'
import type { SessionStore } from './session'

export type HttpOptions = {
  /** Server root without /api (empty: same origin, the dev proxy). */
  baseUrl: string
  session: SessionStore
  /** Called when the session cannot be renewed (go to the sign-in screen). */
  onSignedOut?: () => void
  /** Delays between retries of a command after a network failure. */
  retryDelays?: number[]
  fetch?: typeof fetch
  sleep?: (ms: number) => Promise<void>
}

export type RequestOptions = { signal?: AbortSignal; auth?: boolean }

const uuid = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
      })

export const newRequestId = uuid

/** How many times a rate-limited read is repeated. */
const READ_RETRIES = 4

/** One HTTP client: base URL, bearer token, one shared session refresh on
 *  401, `request_id` on commands with safe retries, typed errors. */
export class Http {
  private refreshing: Promise<boolean> | null = null
  private readonly doFetch: typeof fetch
  private readonly sleep: (ms: number) => Promise<void>
  readonly session: SessionStore

  constructor(private readonly opts: HttpOptions) {
    this.session = opts.session
    this.doFetch = opts.fetch ?? ((input, init) => fetch(input, init))
    this.sleep = opts.sleep ?? (ms => new Promise(r => setTimeout(r, ms)))
  }

  url(path: string): string {
    return this.opts.baseUrl.replace(/\/$/, '') + path
  }

  /** A read. Reads are safe to repeat, so a `429 rate_limited` waits the
   *  server's `retry_after_ms` and tries again (a burst of snapshot re-reads
   *  after events must not leave the screen with an error). */
  async get<T>(path: string, o: RequestOptions = {}): Promise<T> {
    for (let attempt = 0; ; attempt++) {
      try {
        return await this.send<T>('GET', path, undefined, o)
      } catch (e) {
        if (!(e instanceof ApiError) || e.code !== 'rate_limited' || attempt >= READ_RETRIES || o.signal?.aborted) throw e
        // Parallel reads were refused together: spread their repeats so they do not collide again.
        const wait = Number(e.details.retry_after_ms)
        const base = Math.max(Number.isFinite(wait) ? wait : 1_000, 250 * (attempt + 1))
        await this.sleep(Math.min(5_000, base + Math.floor(Math.random() * 250)))
      }
    }
  }

  /** A plain POST (auth routes): no request_id, no retries. */
  post<T>(path: string, body: unknown, o: RequestOptions = {}): Promise<T> {
    return this.send<T>('POST', path, body, o)
  }

  /** A journaled command: adds request_id and repeats the same request
   *  after network failures (the server answers a repeat with the saved reply). */
  async command<T>(path: string, body: Record<string, unknown> = {}, o: RequestOptions = {}): Promise<T> {
    const payload = { request_id: uuid(), ...body }
    const delays = this.opts.retryDelays ?? [400, 1200, 2500]
    for (let attempt = 0; ; attempt++) {
      try {
        return await this.send<T>('POST', path, payload, o)
      } catch (e) {
        if (!(e instanceof ApiError) || e.code !== 'network' || attempt >= delays.length || o.signal?.aborted) throw e
        await this.sleep(delays[attempt])
      }
    }
  }

  /** Multipart upload (admin media). */
  upload<T>(path: string, form: FormData, o: RequestOptions = {}): Promise<T> {
    return this.send<T>('POST', path, form, o)
  }

  /** Renews the session once for all waiting requests. */
  refresh(): Promise<boolean> {
    if (!this.refreshing) {
      this.refreshing = this.doRefresh().finally(() => {
        this.refreshing = null
      })
    }
    return this.refreshing
  }

  private async doRefresh(): Promise<boolean> {
    const token = this.session.refreshToken
    if (!token) return false
    try {
      const s = await this.send<Session>('POST', '/api/v1/auth/refresh', { refresh_token: token }, { auth: false })
      this.session.set(s)
      return true
    } catch (e) {
      if (e instanceof ApiError && e.code === 'network') throw e
      this.session.clear()
      this.opts.onSignedOut?.()
      return false
    }
  }

  private async send<T>(method: string, path: string, body: unknown, o: RequestOptions, retried = false): Promise<T> {
    const auth = o.auth ?? true
    if (auth && !this.session.accessToken && this.session.refreshToken && !retried) {
      await this.refresh()
    }
    const headers: Record<string, string> = { Accept: 'application/json' }
    let payload: BodyInit | undefined
    if (body instanceof FormData) payload = body
    else if (body !== undefined) {
      headers['Content-Type'] = 'application/json'
      payload = JSON.stringify(body)
    }
    const token = this.session.accessToken
    if (auth && token) headers.Authorization = `Bearer ${token}`
    let res: Response
    try {
      res = await this.doFetch(this.url(path), { method, headers, body: payload, signal: o.signal })
    } catch (e) {
      if (o.signal?.aborted) throw e
      throw new ApiError(0, 'network', 'Связь потеряна. Попробуйте ещё раз')
    }
    const receivedAt = Date.now()
    const text = await res.text()
    let data: unknown = undefined
    if (text) {
      try {
        data = JSON.parse(text)
      } catch {
        data = undefined
      }
    }
    if (res.ok) {
      const now = (data as { server_now?: string } | undefined)?.server_now
      serverClock.observe(now, receivedAt)
      return data as T
    }
    const err = (data as { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | undefined)?.error
    const apiErr = new ApiError(res.status, (err?.code ?? 'unknown') as ApiError['code'], err?.message ?? res.statusText, err?.details ?? {})
    if (res.status === 401 && auth && !retried && (apiErr.code === 'unauthorized' || apiErr.code === 'session_revoked')) {
      if (await this.refresh()) return this.send<T>(method, path, body, o, true)
    }
    throw apiErr
  }
}
